import { getFilterFromRules, getRulesFromFilter } from '@src/utils';
import { ALL_TRAFFIC, DASHBOARD_TYPE, isAppModule } from '@src/utils/constants';
import { createClientTempDashboard } from '@src/utils/dashboard/service';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { logger } from '@src/utils/misc/logger';
import moment from 'moment';
import {
  getDistributionDimensionByMetric,
  getMetricByDistributionDimension,
} from '../metrics-panel-hz/utils/metricsMapping';
import { ERROR_DIMENSION_MAP } from '@src/utils/dimension/errorCodeHelper';
import { getFlowDrilldownMetrics } from '../widget-panel/widgets/flow-widget/utils';
import { getFlowConfig } from '@src/hooks/flow/useFlowConfigs';
import { uniq } from 'lodash';
import { getAppFlowUsecaseKeyFromFlowId } from '@src/utils/preset';
import { ECO_WIDGET_TYPES } from '../widget-panel/config/eco';
import { APP_DIMENSIONS } from '@src/utils/dimension/appDimensionHelper';
import { notification } from 'antd';

export const AUTHOR_NEXA_AGENT = 'nexa_agent';
export const AUTHOR_USER = 'user';

// ============================================================================
// ChatBox Utility Types and Functions
// ============================================================================

// Event detail types for custom events
export interface TemplateEventDetail {
  query: string;
  analysis_id?: number;
  deep_mode?: boolean;
}

// Fetch stream parameter type (matches useChatStream)
export interface FetchStreamParams {
  query: string;
  diagnostic?: {
    type: string;
    params?: any;
  };
  analysis_id?: number;
}

export interface ReportStreamEvent {
  stream_id?: string;
  tool_name?: string;
  mode?: 'create' | 'revise' | string;
  phase?: 'start' | 'context_loaded' | 'generating' | 'saving' | 'done' | 'error' | string;
  message?: string;
  seq?: number;
  delta_html?: string;
  is_final_chunk?: boolean;
  version?: number;
  error?: string;
  timestamp?: string;
}

export interface AgentStatusStreamEvent {
  stream_id?: string;
  phase?:
    | 'preparing_context'
    | 'planning'
    | 'fetching_data'
    | 'composing_answer'
    | 'finalizing'
    | 'completed'
    | 'error'
    | string;
  message?: string;
  seq?: number;
  is_heartbeat?: boolean;
  timestamp?: string;
}

export const AGENT_STATUS_PHASE_ORDER: Record<string, number> = {
  preparing_context: 1,
  planning: 2,
  fetching_data: 3,
  composing_answer: 4,
  finalizing: 5,
};

/**
 * Checks if a diagnostic type is marked as "deep" (longer runtime)
 * @param diagnosticType - The diagnostic type to check
 * @param diagnosticModes - Array of diagnostic mode definitions
 * @returns true if the diagnostic is deep, false otherwise
 */
export const isDeepDiagnostic = (
  diagnosticType: string,
  diagnosticModes: Array<{ type: string; deep?: boolean }>,
): boolean => {
  return Boolean(diagnosticModes.find((m) => m.type === diagnosticType)?.deep);
};

/**
 * Shows a notification for long-running deep analysis
 */
export const notifyLongerRuntime = () => {
  notification.info({
    message:
      'This analysis runs a more comprehensive investigation and may take longer to complete.',
    placement: 'topRight',
    duration: 6,
  });
};

/**
 * Builds fetch parameters for initial query from Ask Nexa integration
 * @returns Object with fetchParams and shouldNotify flag
 */
export const buildInitialQueryParams = (
  initialQuery: string,
  askRequest: { diagnostic?: { type: string; params?: any } } | undefined,
  diagnosticModes: Array<{ type: string; deep?: boolean }>,
): { fetchParams: FetchStreamParams; shouldNotify: boolean } => {
  const fetchParams: FetchStreamParams = { query: initialQuery };
  let shouldNotify = false;

  if (askRequest?.diagnostic) {
    const isDeep = isDeepDiagnostic(askRequest.diagnostic.type, diagnosticModes);
    shouldNotify = isDeep;
    fetchParams.diagnostic = askRequest.diagnostic;
  }

  return { fetchParams, shouldNotify };
};

/**
 * Builds fetch parameters from template event detail
 * @returns Object with fetchParams and shouldNotify flag
 */
export const buildTemplateQueryParams = (
  detail: TemplateEventDetail,
): { fetchParams: FetchStreamParams; shouldNotify: boolean } => {
  const fetchParams: FetchStreamParams = { query: detail.query };

  if (detail.analysis_id) {
    fetchParams.analysis_id = detail.analysis_id;
  }

  // Deep mode comes directly from template event
  const shouldNotify = detail.deep_mode === true;

  return { fetchParams, shouldNotify };
};

/*
// args from nexa agent:
metricFilter: [{
  "lower": 1000,
  "type": "bound",
  "upper": 10000,
  "metric": "VideoStartFailures"
}]

// trends format
metricFilter: [{
  "metric": "VideoStartFailuresTech",
  "rule": {
    "type": "bound",
    "dimension": "LifeVSFTWaitTimeMs",
    "upper": 10000,
    "lower": 1000
  },
  "meta": {}
}]
*/

/*
sample format:
{"id":"adk-68ac8c10-12e5-4fc5-82ad-328bf345fe38","args":{"metrics":["Plays"],"type":"group-by","groupBy":["appVersion"],"interval":"2025-07-02T04:38:00Z/2025-07-02T10:38:00Z","dataset":"ExperienceInsights","sortBy":[{"order":"desc","metric":"Plays"}],"granularity":"ALL","limit":10,"filter":[[{"value":"Roku","field":"m3.dv.n","op":"eq"}]]},"name":"metrics_query"}
{"id":"adk-0c291b33-cee4-41e8-9b9a-bcea1c0e38b9","args":{"dataset":"ExperienceInsights","type":"timeseries","granularity":"PT1M","filter":[],"interval":"2025-07-02T09:35:11.590733-06:00/2025-07-02T15:35:11.590733-06:00","metrics":["Attempts"]},"name":"metrics_query"}
*/
export const extractArgs = (rawConfig) => {
  const {
    args: {
      metrics = [],
      groupBy: dimensions = [],
      interval,
      sortBy = [],
      filter = [],
      metricFilter = null,
      flowId = null,
      // Derived keys from merge operations
      comparisonInterval = null,
      compareFilters = null,
    } = {},
  } = rawConfig;

  let orderBy = null;

  // Split interval into from and to, and format using moment
  const [from, to] = interval?.split('/') || [];
  const formattedInterval = {
    from: moment(from).format(),
    to: moment(to).format(),
  };

  // Push the metric from sortBy into metrics
  sortBy?.forEach(({ metric }) => {
    if (metric && !metrics.includes(metric)) {
      metrics.push(metric);
    }
    orderBy = metric;
  });

  return {
    metrics,
    dimensions,
    interval: formattedInterval,
    orderBy,
    filter,
    metricFilter,
    flowId,
    // Pass through derived keys
    comparisonInterval,
    compareFilters,
  };
};

// in some cases the filter rule display name is not set
export const fixFilterFieldNames = (filter) => {
  return filter.map((group) => {
    return group.map((rule) => {
      if (!rule.display) {
        rule.display = rule.value;
      }
      return rule;
    });
  });
};

export const parseMetricFilter = (metricFilter) => {
  const parsedMetricFilter: any[] = [];
  const extractedMetrics: string[] = [];

  if (metricFilter && metricFilter.length > 0) {
    metricFilter.forEach((mf) => {
      let metricKey: string | null = null;
      let dimension: string | null = null;

      // New format: dimension provided, need to find metric
      if (mf.dimension) {
        metricKey = getMetricByDistributionDimension(mf.dimension);
        dimension = mf.dimension;

        if (!metricKey) {
          logger.log('Could not find metric for dimension:', mf.dimension);
          return; // Skip this filter
        }
      }
      // Old format: metric provided, need to find dimension
      else if (mf.metric) {
        metricKey = mf.metric;
        const distributionDimensions = getDistributionDimensionByMetric(mf.metric);
        dimension = distributionDimensions?.[0] || null;

        if (!dimension) {
          logger.log('Could not find dimension for metric:', mf.metric);
          return; // Skip this filter
        }
      } else {
        // Neither dimension nor metric provided, skip
        logger.log('Metric filter entry missing both dimension and metric fields:', mf);
        return;
      }

      // Collect the metric key for adding to metrics list
      if (metricKey) {
        extractedMetrics.push(metricKey);
      }

      // Build the filter rule
      const rule: any = {
        type: mf.type,
        dimension: dimension,
      };

      // Add type-specific fields
      if (mf.type === 'bound') {
        if (mf.lower !== undefined) rule.lower = mf.lower;
        if (mf.upper !== undefined) rule.upper = mf.upper;
      } else if (mf.type === 'equal') {
        rule.value = mf.value;
      }

      // Build meta object - mark as boolean if it's an equal type with boolean value
      const meta: any = {};
      if (mf.type === 'equal' && typeof mf.value === 'boolean') {
        meta.type = 'boolean';
      }

      parsedMetricFilter.push({
        metric: metricKey,
        rule: rule,
        meta: meta,
      });
    });
  }

  return { parsedMetricFilter, extractedMetrics };
};

const filterMetrics = (metrics: string[]) =>
  uniq(
    metrics.map((m) =>
      getInvertedPercentageMetricByKey({
        metricKey: m,
        defaultValue: m,
      }),
    ),
  );

const errorDimensions = [...Object.keys(ERROR_DIMENSION_MAP), APP_DIMENSIONS.ERROR_EVENT_NAME];

// TODO: validate metrics and dimensions
export const parseConfig = (config) => {
  const {
    dimensions,
    filter: rules,
    interval,
    metrics,
    orderBy,
    metricFilter,
    flowId,
    // Derived keys from merge operations
    comparisonInterval,
    compareFilters, // For filter comparison merged links
  } = config;
  fixFilterFieldNames(rules);

  const allRules = getRulesFromFilter(rules, true);
  const filterToApply = getFilterFromRules(allRules, true);

  const rawMetrics = [...metrics];

  const { parsedMetricFilter, extractedMetrics } = parseMetricFilter(metricFilter);

  // Prepend metric filter metrics to rawMetrics (deduplicated, at the front)
  const uniqueExtractedMetrics = uniq(extractedMetrics);
  uniqueExtractedMetrics.forEach((metric) => {
    if (!rawMetrics.includes(metric)) {
      rawMetrics.unshift(metric);
    }
  });

  let selectedMetric = null;

  if (flowId) {
    const flowConfig = getFlowConfig(flowId);
    const flowMetrics = getFlowDrilldownMetrics(flowConfig);
    rawMetrics.push(...flowMetrics);

    // special case for error event name
    if (dimensions.includes(APP_DIMENSIONS.ERROR_EVENT_NAME)) {
      selectedMetric = flowConfig.metrics.total_error_count;
    }
  }

  const processedMetrics = filterMetrics(rawMetrics);

  // filter out error dimensions as it's added by default by trends
  const filteredDimensions = dimensions.filter((dimension) => !errorDimensions.includes(dimension));

  const dashboardConfig: any = {
    // usecases key also enforces the new drilldown workflow (without injector layer)
    usecaseKey: 'nexa-drilldown',
    metricsChartContainer: {
      metric: selectedMetric || processedMetrics[0],
      metrics: processedMetrics,
    },
    common: {
      appliedFilters: [filterToApply],
      interval,
    },
    summary: {
      drilldownData: [ALL_TRAFFIC, filterToApply],
    },
    'module:trends': {
      base: {
        dimensions: filteredDimensions,
        ...(orderBy ? { globalOrderBy: orderBy } : {}),
        // dimensionMetrics: validateMetric([]),
        metricFilter: parsedMetricFilter,
      },
    },
    metricPanel: {
      widgetPanelVisibility: false,
    },
    toggles: {
      sections: {
        summary: false,
      },
    },
  };

  // workaround because flow logic is complex
  if (flowId) {
    dashboardConfig.usecaseKey = getAppFlowUsecaseKeyFromFlowId(flowId);
    dashboardConfig.widgetMeta = { type: ECO_WIDGET_TYPES.flow, flowId };
  }

  // Handle DoD comparison interval if provided (from merged links)
  if (comparisonInterval) {
    const [compFrom, compTo] = comparisonInterval.split('/');

    // 1. Set comparison interval for Redux store (triggers setComparisonInterval action in sharing/sagas.ts)
    dashboardConfig.comparison = {
      interval: {
        from: moment(compFrom).format(),
        to: moment(compTo).format(),
      },
    };

    // 2. Enable dodAtom via toggles.diagnostics.dod (read by initStore in ClientDrilldownDashboard.tsx)
    dashboardConfig.toggles = {
      ...dashboardConfig.toggles,
      diagnostics: {
        ...(dashboardConfig.toggles?.diagnostics || {}),
        dod: true,
      },
    };
  }

  // Handle compareFilters if provided (from filter comparison merged links)
  if (compareFilters && compareFilters.length > 0) {
    // Add compareFilters to trends base config
    dashboardConfig['module:trends'].base.compareFilters = compareFilters;

    // Hide dimension panel since this is a timeseries comparison view
    dashboardConfig.toggles = {
      ...dashboardConfig.toggles,
      sections: {
        ...(dashboardConfig.toggles?.sections || {}),
        dimension: false,
      },
    };
  }

  logger.log('Parsed dashboard config:', dashboardConfig);

  return dashboardConfig;
};

export const drilldownToConfig = ({ dashboardConfig, id, redirectHandler }) => {
  const type = isAppModule
    ? DASHBOARD_TYPE.APP_EXPERIENCE_DRILLDOWN
    : DASHBOARD_TYPE.TRENDS_DRILLDOWN;

  createClientTempDashboard({
    id,
    type,
    config: dashboardConfig,
    title: `nexa:${id}`,
    options: {
      // newBrowserTab: true,
      redirectHandler,
    },
    meta: {
      source: 'nexa',
    },
  });
};

export const getRecentQuery = ({ events }) => {
  const recentQuery = [...events].reverse().find((rec) => rec.author === 'user')?.value || '';
  return recentQuery;
};
export const supportedEventTypes = ['text', 'function_call', 'function_response'];

// template click event (not a track event)
export const NEXA_QUERY_TEMPLATE_CLICK = 'nexa-query-template-click';
export const NEXA_QUERY_RETRY_CLICK = 'nexa-query-retry-click';

// Action bubble execute event
export const NEXA_ACTION_BUBBLE_EXECUTE = 'nexa-action-bubble-execute';

// Event detail type for action bubble execution
export interface ActionBubbleEventDetail {
  query: string;
}

export enum AgentStatus {
  ADDING_CONTEXT = 'adding_context',
  CALLING_TOOL = 'calling_tool',
  PROCESSING_RESPONSE = 'processing_response',
  THINKING = 'thinking',
  COMPLETED = 'completed',
  ERROR = 'error',
}

// Progressive messaging configuration for each status
// Shows different messages and icons based on elapsed time to indicate progress
export interface ProgressiveMessage {
  icon: string;
  message: string;
}

export interface StatusProgressiveMessages {
  [threshold: number]: ProgressiveMessage;
}

// Progressive messages for each agent status
// Keys are time thresholds in seconds (0, 5, 10, 20)
export const ProgressiveStatusMessages: Record<AgentStatus, StatusProgressiveMessages> = {
  [AgentStatus.ADDING_CONTEXT]: {
    0: { icon: '⚙️', message: 'Preparing your request...' },
    5: { icon: '🔍', message: 'Gathering context and tools...' },
    10: { icon: '📚', message: 'Loading extensive context...' },
    20: { icon: '⏳', message: 'Almost ready — finalizing setup...' },
  },
  [AgentStatus.THINKING]: {
    0: { icon: '🤔', message: 'Analyzing your request...' },
    5: { icon: '💭', message: 'Working through the details...' },
    10: { icon: '🧠', message: 'Thinking — planning the best approach…' },
    20: { icon: '⏳', message: 'Still thinking — crafting response...' },
  },
  [AgentStatus.CALLING_TOOL]: {
    0: { icon: '📊', message: 'Fetching data...' },
    5: { icon: '🔄', message: 'Retrieving large dataset...' },
    10: { icon: '⏱️', message: 'Still fetching — optimizing query...' },
    20: { icon: '⏳', message: 'Taking longer than usual — nearly there...' },
  },
  [AgentStatus.PROCESSING_RESPONSE]: {
    0: { icon: '⚡', message: 'Processing results…' },
    5: { icon: '🔧', message: 'Parsing and organizing the data…' },
    10: { icon: '📈', message: 'Summarizing insights and building tables…' },
    20: { icon: '⏳', message: 'Final touch-ups — formatting for clarity…' },
  },
  [AgentStatus.COMPLETED]: {
    0: { icon: '✅', message: 'Done' },
  },
  [AgentStatus.ERROR]: {
    0: { icon: '❌', message: 'Something went wrong' },
  },
};

// Helper function to get the appropriate message and icon based on elapsed time
export const getProgressiveMessage = (
  status: AgentStatus,
  elapsedSeconds: number,
): ProgressiveMessage => {
  const progressiveMessages = ProgressiveStatusMessages[status];

  // Find the highest threshold that's less than or equal to elapsed time
  const thresholds = Object.keys(progressiveMessages)
    .map(Number)
    .sort((a, b) => b - a); // Sort descending

  for (const threshold of thresholds) {
    if (elapsedSeconds >= threshold) {
      return progressiveMessages[threshold];
    }
  }

  // Fallback to 0 threshold
  return progressiveMessages[0];
};

/**
 * Formats markdown content with properly aligned tables
 *
 * @param markdownText - The raw markdown text to format
 * @returns Formatted markdown text with aligned table pipes
 */
export const formatMarkdownForCopy = (markdownText: string): string => {
  if (!markdownText || typeof markdownText !== 'string') {
    return markdownText;
  }

  const lines = markdownText.split('\n');
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Check if this line looks like a table row (contains pipes)
    if (line.includes('|') && line.trim().length > 0) {
      // Found potential table start, collect all consecutive table rows
      const tableLines: string[] = [];
      let j = i;

      while (j < lines.length && lines[j].includes('|') && lines[j].trim().length > 0) {
        tableLines.push(lines[j]);
        j++;
      }

      if (tableLines.length >= 2) {
        // Format the table
        const formattedTable = formatMarkdownTable(tableLines);
        result.push(...formattedTable);
        i = j;
      } else {
        // Not a table, just add the line as-is
        result.push(line);
        i++;
      }
    } else {
      // Not a table line, add as-is
      result.push(line);
      i++;
    }
  }

  return result.join('\n');
};

/**
 * Formats a markdown table with properly aligned pipes
 *
 * @param tableLines - Array of table row strings
 * @returns Array of formatted table row strings
 */
const formatMarkdownTable = (tableLines: string[]): string[] => {
  // Parse each row into cells
  const rows = tableLines.map((line) => {
    // Remove leading/trailing whitespace and split by pipe
    const cells = line.split('|').map((cell) => cell.trim());
    // Remove empty cells from start and end (common in markdown tables)
    if (cells[0] === '') cells.shift();
    if (cells[cells.length - 1] === '') cells.pop();
    return cells;
  });

  if (rows.length === 0) return tableLines;

  // Calculate maximum width for each column
  const maxColumns = Math.max(...rows.map((row) => row.length));
  const columnWidths: number[] = [];

  for (let col = 0; col < maxColumns; col++) {
    let maxWidth = 0;
    for (const row of rows) {
      if (row[col]) {
        maxWidth = Math.max(maxWidth, row[col].length);
      }
    }
    // Minimum width of 3 for readability
    columnWidths[col] = Math.max(maxWidth, 3);
  }

  // Format each row
  const formattedRows = rows.map((row, rowIndex) => {
    const formattedCells = row.map((cell, colIndex) => {
      const width = columnWidths[colIndex];

      // Check if this is a separator row (contains only hyphens, spaces, and colons)
      const isSeparatorRow = rowIndex === 1 && /^[\s\-:]+$/.test(cell);

      if (isSeparatorRow) {
        // For separator rows, use hyphens
        let separator = '-'.repeat(width);

        // Preserve alignment indicators
        if (cell.startsWith(':') && cell.endsWith(':')) {
          separator = ':' + '-'.repeat(Math.max(0, width - 2)) + ':';
        } else if (cell.startsWith(':')) {
          separator = ':' + '-'.repeat(Math.max(0, width - 1));
        } else if (cell.endsWith(':')) {
          separator = '-'.repeat(Math.max(0, width - 1)) + ':';
        }

        return separator;
      } else {
        // For data rows, pad with spaces
        return cell.padEnd(width);
      }
    });

    return `| ${formattedCells.join(' | ')} |`;
  });

  return formattedRows;
};
